import { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Unstable_Grid2'; // Grid version 2

import { useActivityShopUI } from 'hooks/useActivityShopUI';
import { useLocalization } from 'hooks/useLocalization';
import useSanitizedHTML from 'hooks/useSanitizedHTML';

import ActivityDatePickerPrivate from 'components/activity-display/activity-content/ActivityDatePickerPrivate';
import ActivityDatePickerGroup from 'components/activity-display/activity-content/ActivityDatePickerGroup';
import AddonSwitches from 'components/activity-display/activity-content/AddonSwitches';
import ActivityTypeTabButton from 'components/activity-display/activity-content/ActivityTypeTabButton';
import AddActivityButtonContainer from 'components/activity-display/activity-content/AddActivityButtonContainer';
import Gallery from 'components/activity-display/activity-content/Gallery';
import QuickInfoBar from 'components/activity-display/activity-content/QuickInfoBar';
import PaddingWrap from 'components/shared/PaddingWrap';
import ActivityItemTabs from 'components/activity-display/activity-content/ActivityItemTabs';

import { Activity, GalleryImageItem } from "interfaces/ActivityInterfaces";
import CollapsedAlert from 'components/shared/CollapsedAlert';

interface ActivityContentProps {
	activity: Activity;
	scrollContainerNode?: HTMLDivElement | null;
}

function ActivityContent({ activity, scrollContainerNode }: ActivityContentProps) {
	// Define a function to determine the initial tab based on the activity's cart state and availability
	const { getActivityInteractionData, cartBarWrapperElement } = useActivityShopUI();
	const { getStr } = useLocalization();
	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const leftContentRef = useRef<HTMLDivElement | null>(null);
	const rightContentRef = useRef<HTMLDivElement | null>(null);
	const leftSpacerRef = useRef<HTMLDivElement | null>(null);
	const rightSpacerRef = useRef<HTMLDivElement | null>(null);

	const activityInteractionData = getActivityInteractionData(activity.id);
	const [selectedActivityTypeTab, setSelectedActivityTypeTab] = useState<'group' | 'private'>(
		activityInteractionData?.isAdded && activityInteractionData.addedAs ? activityInteractionData.addedAs : activity.availableAsTourType.group ? 'group' : 'private'
	);

	const galleryImages = activity ? 
		(activity.images.gallery?.map((galleryItem: GalleryImageItem) => ({
			url: galleryItem.url,
			urlThumb: galleryItem.urlThumb,
			caption: galleryItem.caption
		})) || []) 
	: [];

	
    // Function to handle scroll event

	useEffect(() => {
		if (cartBarWrapperElement) {
			//let lastScrollTop = 0; // Declare outside to track between invocations
			let scrollContainer: HTMLDivElement | Window | null;
			let isUsingDialog: boolean;
	
			if (scrollContainerNode) {
				scrollContainer = scrollContainerNode;
				isUsingDialog = true;
			} else {
				scrollContainer = window;
				isUsingDialog = false;
			}

			const handleScroll = () => {
				const leftHeight = leftContentRef.current?.clientHeight ?? 0;
				const rightHeight = rightContentRef.current?.clientHeight ?? 0;
	
				const shorterSideContentRef = leftHeight < rightHeight ? leftContentRef : rightContentRef;
				
				if (shorterSideContentRef.current) {
					const shortSideHeight = shorterSideContentRef.current.clientHeight;

					if (
						shortSideHeight < (
							isUsingDialog ? (
								scrollContainerNode?.clientHeight ?? 0 //Compare with dialog height
							) : (
								window.innerHeight - cartBarWrapperElement.getBoundingClientRect().bottom //Compare with viewport height minus header height
							)
							
					)) { //Only make sticky if all of the short side can be seen
						shorterSideContentRef.current.style.position = 'sticky';

						if (isUsingDialog) {
							shorterSideContentRef.current.style.top = `${0}px`;
						} else { //Using full width, so must stick to bottom of cart bar
							const cartBarBottomPosition = cartBarWrapperElement.getBoundingClientRect().bottom ?? 0;
							shorterSideContentRef.current.style.top = `${cartBarBottomPosition}px`;
						}
					}
				}
			};
	
			scrollContainer?.addEventListener('scroll', handleScroll);
	
			return () => {
				scrollContainer?.removeEventListener('scroll', handleScroll);
			};
		}
	}, [cartBarWrapperElement, scrollContainerNode]);

	function handleTabClick(activityType:'group' | 'private') {
		setSelectedActivityTypeTab(activityType);
	}

	const todayDate = new Date();

	const useTwoSides = (galleryImages.length > 0 || activity.descShort || activity.descLong);
	const hasPrivateAndGroup = activity.availableAsTourType.private && activity.availableAsTourType.group;

	const sanitizedDescShort = useSanitizedHTML(activity.descShort);
	const sanitizedDescLong = useSanitizedHTML(activity.descLong);

	if (!activityInteractionData) return null;

	return (
		<div ref={scrollContainerRef}>
			<Grid container>
				<Grid xs={12} md={useTwoSides ? 6 : 12}>
					<div ref={leftSpacerRef} />
					<div ref={leftContentRef}>
						<Gallery images={galleryImages} />
						<QuickInfoBar activity={activity} />
						{
							sanitizedDescShort.__html ? (
								<div style={{ backgroundColor: '#e9efff' }}>
									<PaddingWrap>
										<div dangerouslySetInnerHTML={sanitizedDescShort} />
									</PaddingWrap>
								</div>
							) : null
						}
						<ActivityItemTabs activity={activity} />
						{
							sanitizedDescLong.__html ? (
								<PaddingWrap>
									<div dangerouslySetInnerHTML={sanitizedDescLong} />
								</PaddingWrap>
							) : null
						}
					</div>
				</Grid>
				<Grid xs={12} md={useTwoSides ? 6 : 12}>
					<div ref={rightSpacerRef} />
					<div ref={rightContentRef}>
						<div>
							<Grid container>
								{ activity.availableAsTourType.group ? (
								<Grid xs={hasPrivateAndGroup ? 6 : 12}>
									<ActivityTypeTabButton activityId={activity.id} activityType="group" isSelected={selectedActivityTypeTab === 'group' ? true : false} onClick={() => {handleTabClick('group')}}/>
								</Grid>
								) : null }
								{ activity.availableAsTourType.private ? (
									<Grid xs={hasPrivateAndGroup ? 6 : 12}>
										<ActivityTypeTabButton activityId={activity.id} activityType="private" isSelected={selectedActivityTypeTab === 'private' ? true : false} onClick={() => {handleTabClick('private')}}/>
									</Grid>
								) : null }
							</Grid>
						</div>
						<PaddingWrap>
							<AddonSwitches activity={activity} selectedActivityTypeTab={selectedActivityTypeTab} activityInteractionData={activityInteractionData} />
							{
								!activityInteractionData.isAdded || activityInteractionData.addedAs === selectedActivityTypeTab ? (
									selectedActivityTypeTab === 'private' ? (
										<ActivityDatePickerPrivate
											activityId={activity.id}
											activityInteractionData={activityInteractionData}
											todayDate={todayDate}
										/>
									) : (
										<ActivityDatePickerGroup
											activityId={activity.id}
											activityType={selectedActivityTypeTab}
											activityInteractionData={activityInteractionData}
											todayDate={todayDate}
										/>
									)
								) : <div style={{ height: '30px' }} />
							}
							<CollapsedAlert isVisible={!activityInteractionData.totalPrice[selectedActivityTypeTab]} severity="error">
								{getStr('Price_unset')}
							</CollapsedAlert>
							<AddActivityButtonContainer activity={activity} selectedActivityTypeTab={selectedActivityTypeTab} activityInteractionData={activityInteractionData} />
						</PaddingWrap>
					</div>
				</Grid>
			</Grid>
		</div>
	);
}



export default ActivityContent;